import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAdminReportDetail, regenerateAdminReport } from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/AdminLayout';
import Alert from '../../components/Alert';

const SPECIAL_LABEL = { none: null, skipped: '跳过' };
const RISK_COLOR = { high: { bg: '#fef2f2', border: '#fca5a5', text: '#dc2626', badge: '#dc2626' }, medium: { bg: '#fffbeb', border: '#fde68a', text: '#92400e', badge: '#d97706' }, low: { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', badge: '#2563eb' } };

function Section({ title, icon, children, style }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 20, overflow: 'hidden', ...style }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1a3a6b' }}>{title}</h2>
      </div>
      <div style={{ padding: '18px 20px' }}>{children}</div>
    </div>
  );
}

function InfoGrid({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px 24px' }}>
      {items.filter(i => i.value).map(({ label, value }) => (
        <div key={label}>
          <p style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>{label}</p>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{value}</p>
        </div>
      ))}
    </div>
  );
}

function ScoreBar({ score, max = 100, color = '#2563eb' }) {
  const pct = Math.min((score / max) * 100, 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 4 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.5s' }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color, minWidth: 48, textAlign: 'right' }}>{score}分</span>
    </div>
  );
}

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString('zh-CN');
}

function getDimColor(score) {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#2563eb';
  if (score >= 40) return '#d97706';
  return '#dc2626';
}

export default function AdminReportDetailPage() {
  const { assessmentId } = useParams();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roundFilter, setRoundFilter] = useState('all');
  const [expandedQ, setExpandedQ] = useState(new Set());
  const [regenerating, setRegenerating] = useState(false);
  const [regenMsg, setRegenMsg] = useState('');

  useEffect(() => {
    getAdminReportDetail(assessmentId)
      .then(({ data: d }) => setData(d))
      .catch(e => setError(e.response?.data?.detail || '加载失败'))
      .finally(() => setLoading(false));
  }, [assessmentId]);

  if (loading) return <AdminLayout title="后台详细报告"><p style={{ color: '#94a3b8' }}>加载中...</p></AdminLayout>;
  if (error || !data) return <AdminLayout title="后台详细报告"><Alert type="error">{error || '数据不存在'}</Alert></AdminLayout>;

  const isAdminRole = ['admin', 'superadmin'].includes(me?.role);
  const answers = data.answer_details || [];
  const scoring = data.scoring || {};
  const dimBreakdown = data.dim_breakdown || [];
  const assessmentPath = data.assessment_path || [];
  const rounds = [...new Set(answers.map(a => a.round))];
  const filtered = roundFilter === 'all' ? answers : answers.filter(a => a.round === +roundFilter);
  const portrait = data.user || {};
  const strategy = data.strategy || {};
  const behavior = data.behavior_summary;
  const riskAlerts = data.risk_alerts;

  const toggleQ = (idx) => {
    setExpandedQ(prev => {
      const s = new Set(prev);
      s.has(idx) ? s.delete(idx) : s.add(idx);
      return s;
    });
  };

  const doRegenerate = async () => {
    if (regenerating) return;
    if (!window.confirm('确定重新生成这份报告？将用最新模型与逻辑重跑文案，覆盖当前内容（分数与排名不变）。约 1-2 分钟完成。')) return;
    setRegenerating(true);
    setRegenMsg('');
    try {
      await regenerateAdminReport(assessmentId);
      setRegenMsg('已触发重新生成，约 1-2 分钟后刷新本页查看新结果。');
    } catch (e) {
      setRegenMsg(e.response?.data?.detail || '触发失败，请重试');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <AdminLayout title={`后台详细报告 #${assessmentId}`}>
      <div style={{ maxWidth: 960 }}>

        {/* ── 管理员操作：重新生成报告（兜底报告补救，用户端 done 态无重试入口） ── */}
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={doRegenerate} disabled={regenerating} style={{
            padding: '8px 18px', borderRadius: 8, border: '1.5px solid #2563eb',
            background: regenerating ? '#eff6ff' : '#2563eb', color: regenerating ? '#2563eb' : '#fff',
            fontSize: 13, fontWeight: 600, cursor: regenerating ? 'default' : 'pointer',
          }}>
            {regenerating ? '触发中…' : '🔄 重新生成报告'}
          </button>
          {regenMsg && <span style={{ fontSize: 13, color: '#16a34a' }}>{regenMsg}</span>}
        </div>

        {/* ── 顶部总览 ── */}
        <div style={{ background: 'linear-gradient(135deg,#1a3a6b,#2d5aa0)', color: '#fff', borderRadius: 12, padding: '20px 28px', marginBottom: 20, display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>AIQ 总分</p>
            <p style={{ fontSize: 48, fontWeight: 900, lineHeight: 1 }}>{scoring.total_score ?? '—'}</p>
          </div>
          <div>
            <p style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>能力等级</p>
            <p style={{ fontSize: 22, fontWeight: 700 }}>{scoring.grade || '—'}</p>
          </div>
          <div style={{ height: 48, width: 1, background: 'rgba(255,255,255,0.2)' }} />
          <div>
            <p style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>用户</p>
            <p style={{ fontSize: 15, fontWeight: 600 }}>{portrait.display_name}</p>
            <p style={{ fontSize: 12, opacity: 0.7 }}>{portrait.email}</p>
          </div>
          <div>
            <p style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>企业</p>
            <p style={{ fontSize: 14, fontWeight: 600 }}>{data.enterprise || '个人用户'}</p>
          </div>
          <div>
            <p style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>策略</p>
            <p style={{ fontSize: 14, fontWeight: 600 }}>{strategy.name}</p>
            <p style={{ fontSize: 12, opacity: 0.7 }}>v{strategy.version}</p>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            {dimBreakdown.slice(0, 4).map(d => (
              <div key={d.dimension_id} style={{ marginBottom: 5 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, opacity: 0.85, marginBottom: 2 }}>
                  <span>{d.dimension_name}</span><span>{d.score}分</span>
                </div>
                <div style={{ height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
                  <div style={{ height: '100%', width: `${d.score}%`, background: '#93c5fd', borderRadius: 2 }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── 风险提醒（管理员专属） ── */}
        {isAdminRole && riskAlerts && riskAlerts.length > 0 && (
          <Section title="风险提醒" icon="🚨" style={{ border: '1px solid #fca5a5' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {riskAlerts.map((r, i) => {
                const c = RISK_COLOR[r.level] || RISK_COLOR.low;
                return (
                  <div key={i} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, padding: '12px 16px', display: 'flex', gap: 12 }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{r.level === 'high' ? '🔴' : r.level === 'medium' ? '🟡' : '🔵'}</span>
                    <div>
                      <p style={{ fontWeight: 700, color: c.text, marginBottom: 3, fontSize: 14 }}>{r.title}</p>
                      <p style={{ color: c.text, fontSize: 13, opacity: 0.85, lineHeight: 1.6 }}>{r.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* ── 用户基础画像 ── */}
        <Section title="用户基础画像" icon="👤">
          <InfoGrid items={[
            { label: '姓名', value: portrait.display_name },
            { label: '邮箱', value: portrait.email },
            { label: '企业', value: data.enterprise || '个人用户' },
            { label: '公司', value: portrait.company },
            { label: '岗位', value: portrait.position },
            { label: '所属行业', value: portrait.industry },
            { label: '工作年限', value: portrait.work_years },
            { label: '岗位类别', value: portrait.job_category },
            { label: '管理层级', value: portrait.management_level },
            { label: 'AI 能力等级（自评）', value: portrait.skill_level },
            { label: 'AI 使用频率', value: portrait.ai_usage_freq },
            { label: '测评模式', value: data.mode === 'enterprise' ? '企业模式' : '个人模式' },
            { label: '测评深度', value: data.depth },
            { label: '开始时间', value: fmtDate(data.started_at) },
            { label: '完成时间', value: fmtDate(data.completed_at) },
          ]} />
          {portrait.ai_usage_purpose && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
              <strong style={{ color: '#64748b' }}>AI 使用目的：</strong>{portrait.ai_usage_purpose}
            </div>
          )}
        </Section>

        {/* ── 策略信息 ── */}
        <Section title="本次测评策略" icon="⚙️">
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ padding: '14px 20px', background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe', minWidth: 160 }}>
              <p style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>策略名称</p>
              <p style={{ fontSize: 16, fontWeight: 700, color: '#1a3a6b' }}>{strategy.name}</p>
            </div>
            <div style={{ padding: '14px 20px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', minWidth: 120 }}>
              <p style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>版本号</p>
              <p style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>v{strategy.version}</p>
            </div>
          </div>
        </Section>

        {/* ── 测评路径摘要 ── */}
        {assessmentPath.length > 0 && (
          <Section title="测评路径摘要" icon="🗺️">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['轮次', '题数', '正常作答', '特殊选项', '正确率', '得分', '得分率'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'center', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assessmentPath.map(p => (
                    <tr key={p.round} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#1a3a6b' }}>第 {p.round} 轮</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>{p.total}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>{p.normal}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center', color: p.special > 0 ? '#d97706' : '#94a3b8' }}>{p.special}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <span style={{ fontWeight: 600, color: p.accuracy >= 60 ? '#16a34a' : '#dc2626' }}>{p.accuracy}%</span>
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>{p.score} / {p.max_score}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <span style={{ fontWeight: 600, color: p.score_rate >= 60 ? '#16a34a' : '#dc2626' }}>{p.score_rate}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* ── 行为特征摘要（管理员专属） ── */}
        {isAdminRole && behavior && (
          <Section title="作答行为特征摘要" icon="📊">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
              {[
                { label: '总题数', value: behavior.total_questions, unit: '题' },
                { label: '正常作答', value: behavior.normal_answered, unit: '题' },
                { label: '特殊选项', value: behavior.special_count, unit: '题' },
                { label: '特殊比率', value: `${behavior.special_rate}%`, unit: '' },
                { label: '正确题数', value: behavior.correct_count, unit: '题' },
                { label: '答题正确率', value: `${behavior.normal_correct_rate}%`, unit: '' },
                { label: '用时', value: behavior.duration_minutes ? `${behavior.duration_minutes} 分钟` : '—', unit: '' },
              ].map(({ label, value, unit }) => (
                <div key={label} style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                  <p style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{label}</p>
                  <p style={{ fontSize: 20, fontWeight: 800, color: '#1a3a6b' }}>{value}{unit}</p>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              {Object.entries(behavior.special_breakdown).map(([label, count]) => count > 0 && (
                <div key={label} style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 16px', textAlign: 'center' }}>
                  <p style={{ fontSize: 18, fontWeight: 800, color: '#d97706' }}>{count}</p>
                  <p style={{ fontSize: 11, color: '#92400e' }}>「{label}」</p>
                </div>
              ))}
            </div>
            {Object.keys(behavior.difficulty_distribution).length > 0 && (
              <div>
                <p style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 8 }}>难度分布：</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.entries(behavior.difficulty_distribution).map(([diff, count]) => (
                    <div key={diff} style={{ background: '#f1f5f9', borderRadius: 6, padding: '4px 12px', fontSize: 12, color: '#374151' }}>
                      难度 {'★'.repeat(+diff || 0)} × {count}题
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* ── 维度详细拆解（维度数由后端决定，未测试维度会被标灰） ── */}
        {dimBreakdown.length > 0 && (
          <Section title={`${dimBreakdown.length} 维度详细拆解`} icon="🎯">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {dimBreakdown.map(d => {
                const notTested = d.not_tested === true;
                const color = notTested ? '#94a3b8' : getDimColor(d.score);
                const borderColor = notTested ? '#e2e8f0' : '#e2e8f0';
                return (
                  <div key={d.dimension_id} style={{ border: `1px solid ${borderColor}`, borderRadius: 10, padding: '14px 18px', background: notTested ? '#f8fafc' : undefined }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: 15, color: notTested ? '#64748b' : '#1a3a6b' }}>
                          {d.dimension_name}
                          {notTested && (
                            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#94a3b8', background: '#e2e8f0', padding: '2px 8px', borderRadius: 4 }}>
                              未测试
                            </span>
                          )}
                        </span>
                        {d.dimension_description && (
                          <p style={{ fontSize: 12, color: '#64748b', marginTop: 2, lineHeight: 1.5 }}>{d.dimension_description}</p>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ fontSize: 10, color: '#94a3b8' }}>题数</p>
                          <p style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>{notTested ? '—' : d.question_count}</p>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ fontSize: 10, color: '#94a3b8' }}>正确率</p>
                          <p style={{ fontSize: 14, fontWeight: 700, color }}>{notTested ? '—' : `${d.accuracy}%`}</p>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <p style={{ fontSize: 10, color: '#94a3b8' }}>特殊</p>
                          <p style={{ fontSize: 14, fontWeight: 700, color: !notTested && d.special_in_dim > 0 ? '#d97706' : '#94a3b8' }}>{notTested ? '—' : d.special_in_dim}</p>
                        </div>
                      </div>
                    </div>
                    <ScoreBar score={notTested ? 0 : d.score} color={color} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                      <span>{notTested ? '本次未抽到该维度题目' : `得分 ${d.earned} / ${d.max}`}</span>
                      <span style={{ color, fontWeight: 600 }}>
                        {notTested ? '未测试' : (d.score >= 80 ? '优秀' : d.score >= 60 ? '良好' : d.score >= 40 ? '待提升' : '明显短板')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* ── 特殊选项分析 ── */}
        {data.special_summary && data.special_summary.total > 0 && (
          <Section title={`特殊选项分析（共 ${data.special_summary.total} 题，占 ${Math.round(data.special_summary.total / data.special_summary.total_questions * 100)}%）`} icon="⚠️">
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
              {Object.entries(data.special_summary.by_type || {}).map(([label, count]) => (
                <div key={label} style={{ background: '#fef3c7', borderRadius: 8, padding: '10px 18px', textAlign: 'center' }}>
                  <p style={{ fontSize: 22, fontWeight: 800, color: '#d97706' }}>{count}</p>
                  <p style={{ fontSize: 12, color: '#92400e' }}>「{label}」</p>
                </div>
              ))}
            </div>
            <div>
              <p style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 8 }}>按维度分布：</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(data.special_summary.by_dimension || {}).map(([dim, counts]) => (
                  <div key={dim} style={{ fontSize: 12, background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 6, padding: '4px 10px', color: '#78350f' }}>
                    <strong>{dim}</strong>：{Object.entries(counts).map(([l, c]) => `${l} ×${c}`).join('、')}
                  </div>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* ── AI 诊断与建议 ── */}
        {scoring.overall_comment && (
          <Section title="AI 综合诊断与建议" icon="🤖">
            <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 18px', marginBottom: 16, fontSize: 14, lineHeight: 1.8, color: '#374151' }}>
              {scoring.overall_comment}
            </div>
            {scoring.strengths?.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#16a34a', marginBottom: 8 }}>✅ 优势领域</p>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#374151', lineHeight: 2 }}>
                  {scoring.strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {scoring.weaknesses?.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#dc2626', marginBottom: 8 }}>⚠️ 薄弱领域</p>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#374151', lineHeight: 2 }}>
                  {scoring.weaknesses.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {scoring.suggestions?.length > 0 && (
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#1a3a6b', marginBottom: 10 }}>💡 提升建议</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {scoring.suggestions.map((s, i) => (
                    <div key={i} style={{ background: '#eff6ff', borderRadius: 8, padding: '10px 14px', border: '1px solid #bfdbfe' }}>
                      <p style={{ fontWeight: 600, fontSize: 13, color: '#1d4ed8', marginBottom: 3 }}>{s.title}</p>
                      <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{s.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* ── 逐题表现明细 ── */}
        <Section title={`逐题表现明细（共 ${answers.length} 题）`} icon="📋">
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>筛选轮次：</span>
            {['all', ...rounds].map(r => (
              <button key={r} onClick={() => setRoundFilter(r)} style={{
                padding: '5px 14px', borderRadius: 8,
                border: `1.5px solid ${roundFilter === r ? '#1a3a6b' : '#e2e8f0'}`,
                background: roundFilter === r ? '#1a3a6b' : '#fff',
                color: roundFilter === r ? '#fff' : '#64748b',
                fontSize: 13, cursor: 'pointer', fontWeight: roundFilter === r ? 600 : 400,
              }}>
                {r === 'all' ? '全部' : `第 ${r} 轮`}
              </button>
            ))}
            <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 4 }}>显示 {filtered.length} 题</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map((a, idx) => {
              const isExpanded = expandedQ.has(idx);
              const isSpecial = a.special_option && a.special_option !== 'none';
              const borderColor = isSpecial ? '#fde68a' : a.is_correct === true ? '#d1fae5' : a.is_correct === false ? '#fee2e2' : '#e2e8f0';
              return (
                <div key={idx} style={{ background: '#fff', borderRadius: 10, border: `1px solid ${borderColor}`, overflow: 'hidden' }}>
                  <div
                    onClick={() => toggleQ(idx)}
                    style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start' }}
                  >
                    <span style={{
                      flexShrink: 0, width: 26, height: 26, borderRadius: '50%', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                      background: isSpecial ? '#fef3c7' : a.is_correct === true ? '#d1fae5' : a.is_correct === false ? '#fee2e2' : '#f1f5f9',
                      color: isSpecial ? '#d97706' : a.is_correct === true ? '#16a34a' : a.is_correct === false ? '#dc2626' : '#64748b',
                    }}>
                      {isSpecial ? '?' : a.is_correct === true ? '✓' : a.is_correct === false ? '✗' : '—'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>第{a.round}轮</span>
                        {a.dimension && <span style={{ fontSize: 11, background: '#eff6ff', color: '#2563eb', padding: '1px 7px', borderRadius: 4 }}>{a.dimension}</span>}
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>难度{'★'.repeat(a.difficulty || 0)}</span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>得分 {a.score_earned}/{a.max_score}</span>
                        {isSpecial && <span style={{ fontSize: 11, background: '#fef3c7', color: '#d97706', padding: '1px 7px', borderRadius: 4 }}>「{SPECIAL_LABEL[a.special_option]}」</span>}
                      </div>
                      <p style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.6, margin: 0 }}>
                        {a.stem.length > 80 && !isExpanded ? a.stem.slice(0, 80) + '…' : a.stem}
                      </p>
                    </div>
                    <span style={{ fontSize: 12, color: '#94a3b8', flexShrink: 0, marginTop: 2 }}>{isExpanded ? '▲' : '▼'}</span>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '0 16px 14px 52px', fontSize: 13 }}>
                      {Object.keys(a.options || {}).length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          {Object.entries(a.options).map(([k, v]) => {
                            const isSelected = (a.selected_options || []).includes(k);
                            const isCorrect = (a.correct_answer || []).includes(k);
                            let bg = 'transparent', color = '#374151', fontWeight = 400;
                            if (isCorrect) { bg = '#f0fdf4'; color = '#16a34a'; fontWeight = 600; }
                            if (isSelected && !isCorrect) { bg = '#fef2f2'; color = '#dc2626'; fontWeight = 600; }
                            if (isSelected && isCorrect) { bg = '#f0fdf4'; color = '#16a34a'; fontWeight = 700; }
                            return (
                              <div key={k} style={{ padding: '5px 10px', borderRadius: 6, marginBottom: 3, background: bg, color, fontWeight, lineHeight: 1.6 }}>
                                <strong>{k}. </strong>{v}
                                {isCorrect && ' ✓'}
                                {isSelected && !isCorrect && ' ✗'}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 16, fontSize: 12, flexWrap: 'wrap', color: '#64748b' }}>
                        <span>作答：{isSpecial
                          ? <span style={{ color: '#d97706' }}>「{SPECIAL_LABEL[a.special_option]}」</span>
                          : <strong style={{ color: '#1a3a6b' }}>{(a.selected_options || []).join(', ') || '—'}</strong>}</span>
                        <span>正确答案：<strong style={{ color: '#16a34a' }}>{(a.correct_answer || []).join(', ')}</strong></span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        <div style={{ marginTop: 8, paddingBottom: 20 }}>
          <Link to="/admin/users" style={{ color: '#2563eb', fontSize: 13 }}>← 返回用户管理</Link>
        </div>
      </div>
    </AdminLayout>
  );
}
